import * as React from 'react';
import styled from 'styled-components';
import {LessonObject} from 'api/lesson/types';
import LessonPreview, {LessonPreviewProps} from 'components/LessonPreview';
import {IconClip, IconCopy, IconEdit, IconTrash} from '../icons';
import ControlButton from './ControlButton';
import {useRoute} from 'react-router5';
import ProgramCardUsageList from "./ProgramCardUsageList";

export interface TeacherLessonPreviewProps
  extends LessonPreviewProps {
  onCopy?(id: LessonObject['id']): void;
  onDelete?(id: LessonObject['id']): void;
}

const Controls = styled.div`
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

const UsageListWrapper = styled.div`
  position: relative;
`;

const UsageListBox = styled.div`
  position: absolute;
  top: calc(100% + 15px);
  right: -125px;
  z-index: 100;
`;

/**
 * This component renders a preview of the lesson from the teacher's view.
 * @param {TeacherLessonPreviewProps} props
 * @return {React.ReactElement<TeacherLessonPreviewProps>}
 * @constructor
 */
export default function TeacherLessonPreview(
    props: TeacherLessonPreviewProps,
): React.ReactElement<TeacherLessonPreviewProps> {
  const {router} = useRoute();
  const [usageListOpened, setUsageListOpened] = React.useState(false)

  /**
   * Render controls.
   * @return {JSX.Element}
   */
  function renderControls(): JSX.Element {
    /**
     * Render usage list control.
     * @return {JSX.Element}
     */
    function renderUsageControl(): JSX.Element {
      /**
       * This click event handler.
       */
      function handleClick(): void {
        setUsageListOpened((prev) => !prev)
      }

      /**
       * render usage list
       */
      function renderUsageList(): JSX.Element | void {
        if (usageListOpened) {
          return (
            <UsageListBox>
              <ProgramCardUsageList
                type='cards'
                itemId={props.id}
                onIconCloseClick={() => setUsageListOpened(false)}
              />
            </UsageListBox>
          )
        }
      }

      return (
        <UsageListWrapper>
          <ControlButton onClick={handleClick}>
            <IconClip />
          </ControlButton>
          {renderUsageList()}
        </UsageListWrapper>
      );
    }
    /**
     * Render edit control.
     * @return {JSX.Element}
     */
    function renderEditControl(): JSX.Element {
      /**
       * This click event handler.
       */
      function handleClick(): void {
        router.navigate('lessons.lesson.edit', {
          id: props.id,
        });
      }

      return (
        <ControlButton onClick={handleClick}>
          <IconEdit />
        </ControlButton>
      );
    }

    /**
     * Render copy control.
     * @return {JSX.Element}
     */
    function renderCopyControl(): JSX.Element {
      /**
       * This click event handler.
       */
      function handleClick(): void {
        props.onCopy && props.onCopy(props.id);
      }

      return (
        <ControlButton onClick={handleClick}>
          <IconCopy />
        </ControlButton>
      );
    }

    /**
     * Render trash control.
     * @return {JSX.Element}
     */
    function renderTrashControl(): JSX.Element {
      /**
       * This click event handler.
       */
      function handleClick(): void {
        props.onDelete && props.onDelete(props.id);
      }

      return (
        <ControlButton onClick={handleClick}>
          <IconTrash />
        </ControlButton>
      );
    }

    return (
      <Controls>
        {renderUsageControl()}
        {renderEditControl()}
        {renderCopyControl()}
        {renderTrashControl()}
      </Controls>
    );
  }
  return (
    <LessonPreview
      {...props}
      renderControls={renderControls()}
    />
  );
}
